import { z } from 'zod';
import { addMonthsToDate, dateString, dateStringSchema } from './dateString.js';
import { defined } from './defined.js';
import { runRateForecastEnd, runRateForecastStart, runRateForecastTerm, } from './expenseForecast.js';
import { INCOME_CLASSES, INCOME_FUNDS, INTEREST_HELD_IN, ONE_TIME_INCOME_STATUSES, } from './incomeCommitments.js';
import { allPayments, categoriseAmount, constCond, constDate, describeAmount, extractLineItems, once, projectedDueDates, recurrenceFor, repeat, scaleAmount, taxonomyRefSchema, unit, whenAmount, } from './payments/payments.js';
export const incomeClassSchema = z.enum(INCOME_CLASSES);
export const incomeFundSchema = z.enum(INCOME_FUNDS);
const draftBase = {
    name: z.string().trim().min(1).max(200),
    taxonomy: taxonomyRefSchema,
    incomeClass: incomeClassSchema,
    fund: incomeFundSchema,
};
export const incomeCommitmentDraftSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('recurring'),
        ...draftBase,
        monthlyAmount: z.number().positive(),
        startDate: dateStringSchema,
        endDate: dateStringSchema.optional(),
    }),
    z.object({
        kind: z.literal('one_time'),
        ...draftBase,
        amount: z.number().positive(),
        date: dateStringSchema,
        status: z.enum(ONE_TIME_INCOME_STATUSES),
    }),
    z.object({
        kind: z.literal('activity'),
        ...draftBase,
        fee: z.number().positive(),
        countPerYear: z.number().positive(),
        basis: z.string().trim().max(200),
    }),
    z.object({
        kind: z.literal('interest'),
        ...draftBase,
        balance: z.number().positive(),
        yieldPercent: z.number().min(0).max(100),
        maturityDate: dateStringSchema.optional(),
        rateAfterPercent: z.number().min(0).max(100).optional(),
        heldIn: z.enum(INTEREST_HELD_IN),
    }),
    z.object({
        kind: z.literal('special_assessment'),
        ...draftBase,
        monthlyAmount: z.number().positive(),
        startDate: dateStringSchema,
        endDate: dateStringSchema,
    }),
]);
export const incomeCommitmentMetadataSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('one_time'), status: z.enum(ONE_TIME_INCOME_STATUSES) }),
    z.object({
        kind: z.literal('activity'),
        fee: z.number().positive(),
        countPerYear: z.number().positive(),
        basis: z.string().max(200),
    }),
    z.object({
        kind: z.literal('interest'),
        balance: z.number().positive(),
        yieldPercent: z.number().min(0).max(100),
        maturityDate: z.string().optional(),
        rateAfterPercent: z.number().min(0).max(100).optional(),
        heldIn: z.enum(INTEREST_HELD_IN),
    }),
]);
function metadataAmount(metadata, amount) {
    return describeAmount(JSON.stringify(metadata), amount);
}
function monthlyTerm(taxonomy, description, amount, startDate, endDate, metadata) {
    const scaled = scaleAmount(amount, unit());
    return repeat(recurrenceFor('monthly', 1, startDate), categoriseAmount(taxonomy, description, metadata === undefined ? scaled : metadataAmount(metadata, scaled)), 'USD', startDate, endDate);
}
function oneTimeTerm(draft) {
    const scaled = scaleAmount(draft.amount, unit());
    const counted = draft.status === 'expected'
        ? scaled
        : whenAmount(constCond(draft.status === 'received'), scaled);
    return once(constDate(draft.date), categoriseAmount(draft.taxonomy, draft.name, metadataAmount({ kind: 'one_time', status: draft.status }, counted)));
}
function interestTerm(draft, today) {
    const start = runRateForecastStart(today);
    const end = runRateForecastEnd(today);
    const metadata = {
        kind: 'interest',
        balance: draft.balance,
        yieldPercent: draft.yieldPercent,
        heldIn: draft.heldIn,
        ...defined({ maturityDate: draft.maturityDate, rateAfterPercent: draft.rateAfterPercent }),
    };
    const monthlyAt = (percent, from, until) => monthlyTerm(draft.taxonomy, draft.name, (draft.balance * percent) / 100 / 12, from, until, metadata);
    if (draft.maturityDate === undefined || draft.maturityDate >= end) {
        return monthlyAt(draft.yieldPercent, start, end);
    }
    const afterStart = addMonthsToDate(dateString(`${draft.maturityDate.slice(0, 7)}-01`), 1);
    const afterRate = draft.rateAfterPercent ?? draft.yieldPercent;
    if (draft.maturityDate < start)
        return monthlyAt(afterRate, start, end);
    return allPayments(monthlyAt(draft.yieldPercent, start, draft.maturityDate), monthlyAt(afterRate, afterStart, end));
}
export function incomeCommitmentTerm(draft, today) {
    switch (draft.kind) {
        case 'recurring':
        case 'special_assessment':
            return monthlyTerm(draft.taxonomy, draft.name, draft.monthlyAmount, draft.startDate, draft.endDate);
        case 'one_time':
            return oneTimeTerm(draft);
        case 'activity':
            return monthlyTerm(draft.taxonomy, draft.name, (draft.fee * draft.countPerYear) / 12, runRateForecastStart(today), runRateForecastEnd(today), {
                kind: 'activity',
                fee: draft.fee,
                countPerYear: draft.countPerYear,
                basis: draft.basis,
            });
        case 'interest':
            return interestTerm(draft, today);
        default: {
            const _exhaustive = draft;
            throw new Error(`Unhandled income commitment: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
export function incomeRunRateTerm(taxonomy, monthlyAmount, today) {
    return runRateForecastTerm(taxonomy, taxonomy.subcategory, monthlyAmount, 0, today);
}
export function incomeDueDates(payment, windowStart, windowEnd) {
    switch (payment.kind) {
        case 'zero':
            return [];
        case 'all':
            return payment.payments.flatMap((part) => incomeDueDates(part, windowStart, windowEnd));
        case 'once':
        case 'repeat':
            return projectedDueDates(payment, windowStart, windowEnd, windowStart).map((dueDate) => ({
                dueDate,
                amount: extractLineItems(payment.amount, { currentDate: dueDate, dueDate }).reduce((sum, line) => sum + line.amount, 0),
            }));
        case 'first':
        case 'notToExceed':
            throw new Error(`An income commitment term has no '${payment.kind}' payment.`);
        default: {
            const _exhaustive = payment;
            throw new Error(`Unhandled payment: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
//# sourceMappingURL=incomeCommitmentTerm.js.map